// client/src/lib/settingsApi.ts
import { API_BASE_URL, get, put } from './apiClient';

export interface ExchangeRateData {
  // D102.7: both are null until a rate is set in Settings — there is no default.
  rate: number | null; // TTD to USD rate (e.g., 0.1667 for 6 TTD = 1 USD)
  usdToTtd: number | null; // USD to TTD rate (e.g., 6 for 1 USD = 6 TTD)
  lastUpdated: string | null;
  updatedBy: string | null;
}

export interface ExchangeRateResponse {
  success: boolean;
  data: ExchangeRateData;
}

/** One entry in the exchange-rate change log. */
export interface ExchangeRateHistoryEntry {
  _id: string;
  usdToTtd: number; // 1 USD = x TTD
  ttdToUsd: number | null; // 1 TTD = x USD
  effectiveFrom: string;
  effectiveTo: string | null; // null while this is the rate in force
  isActive: boolean;
  setByName: string;
  notes: string;
  createdAt: string;
}

/**
 * T&T's VAT rate, admin-changeable (owner rule, 2026-09-24: "one Trinidad VAT
 * rate", the government can change it). `ratePercent: null` means no admin
 * has ever set one — every caller (`costPriceTiers.catalogueRowCostPrice` and
 * its callers) treats that as "cannot gross a VAT-applicable row", never as
 * 0% or a guessed 12.5%.
 */
export interface VatRateData {
  ratePercent: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** One entry in the VAT-rate change log, newest first. */
export interface VatRateHistoryEntry {
  ratePercent: number;
  previousRatePercent: number | null;
  changedAt: string;
  changedBy: string | null;
}

export interface IfsRfqSettings {
  enabled: boolean;
  matchFromEmailDomains: string[];
  matchSubjectKeywords: string[];
  priceColumnKeywords: string[];
  treatZeroAsMissing: boolean;
}

export interface IfsRfqSettingsResponse {
  success: boolean;
  data: IfsRfqSettings;
}

export interface ConfirmedOrderSettings {
  enabled: boolean;
  autoDetect: boolean;
  allowManual: boolean;
  // D45.5/D45.6: the quotation statuses and senders an email may confirm are fixed behaviour on
  // the server, not settings.
  timeWindowDays: number;
  matchSubjectKeywords: string[];
  matchBodyKeywords: string[];
  excludeKeywords: string[];
}

export interface ConfirmedOrderSettingsResponse {
  success: boolean;
  data: ConfirmedOrderSettings;
}

export type EmailInboxType =
  | 'rfq'
  | 'quotation'
  | 'price_list'
  | 'confirmed_order'
  | 'others';

export interface EmailSubjectRule {
  category: EmailInboxType;
  keywords: string[];
}

export interface EmailInboxConfig {
  id: string;
  name: string;
  enabled: boolean;
  provider: 'imap';
  mailbox: string;
  types: EmailInboxType[];
  imap: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
    hasPassword: boolean;
  };
}

export interface EmailInboxSettingsResponse {
  success: boolean;
  data: { inboxes: EmailInboxConfig[]; subjectRules?: EmailSubjectRule[] };
}

export interface InboxClassifierSettings {
  enabled: boolean;
  maxChars: number;
  temperature: number;
  model: string;
  lastRunAt?: string | null;
  lastRunStats?: {
    processed?: number;
    updated?: number;
    dryRun?: boolean;
    scope?: string;
  } | null;
}

/**
 * Get the current TTD to USD exchange rate from system settings
 */
export async function getExchangeRate(): Promise<ExchangeRateData> {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/settings/exchange-rate`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }
    
    const data: ExchangeRateResponse = await response.json();
    
    if (!data.success) {
      throw new Error('Failed to fetch exchange rate');
    }
    
    return data.data;
  } catch (error: any) {
    console.error('Error fetching exchange rate:', error);
    throw new Error(error.message || 'Failed to fetch exchange rate');
  }
}

/**
 * Exchange rate change log, newest first.
 */
export async function getExchangeRateHistory(
  limit = 50
): Promise<ExchangeRateHistoryEntry[]> {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(
      `${API_BASE_URL}/settings/exchange-rate/history?limit=${limit}`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }

    const data: { success: boolean; data: ExchangeRateHistoryEntry[] } =
      await response.json();

    if (!data.success) {
      throw new Error('Failed to fetch exchange rate history');
    }

    return data.data || [];
  } catch (error: any) {
    console.error('Error fetching exchange rate history:', error);
    throw new Error(error.message || 'Failed to fetch exchange rate history');
  }
}

/**
 * Update the TTD to USD exchange rate in system settings.
 * Applies to quotations generated after the change only — existing quotations
 * keep the rate they were locked at.
 * @param rate - The TTD to USD rate (e.g., 0.1667 for 6 TTD = 1 USD)
 * @param updatedBy - Optional user identifier
 * @param notes - Optional reason, stored on the change log entry
 */
export async function updateExchangeRate(
  rate: number,
  updatedBy?: string,
  notes?: string
): Promise<ExchangeRateData> {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/settings/exchange-rate`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ rate, updatedBy, notes }),
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }
    
    const data: ExchangeRateResponse = await response.json();
    
    if (!data.success) {
      throw new Error('Failed to update exchange rate');
    }
    
    return data.data;
  } catch (error: any) {
    console.error('Error updating exchange rate:', error);
    throw new Error(error.message || 'Failed to update exchange rate');
  }
}

/**
 * The current Trinidad VAT rate (BUSINESS_RULES#vat-rate — one owner, the
 * Settings row `costPriceTiers.catalogueRowCostPrice` and everything that
 * calls it reads). `ratePercent: null` means no admin has ever set one.
 *
 * On `apiClient.get` (CLAUDE.md: new API code goes through
 * `lib/apiClient.ts`, never a second fetch wrapper). This used to catch its
 * own fetch and re-`throw new Error(error.message || …)` — a PLAIN Error,
 * which drops the machine-readable `code` (e.g. `VAT_RATE_NOT_SET`)
 * `apiClient.handleResponse` already stamps on its own thrown error
 * (invariant 6: a caller switches on `err.code`, never on message prose —
 * see `QuotationDetailPage.handleRefreshLinePrice`'s own `err.code ===
 * 'VAT_RATE_NOT_SET'` check). Letting `apiClient`'s error propagate
 * unchanged is what keeps that code reachable.
 */
export async function getVatRate(): Promise<VatRateData> {
  const res = await get<{ success: boolean; data: VatRateData }>('/settings/vat-rate');
  return res.data;
}

/**
 * Set the Trinidad VAT rate. `restatedRows` (on the response) is how many
 * Master Catalogue rows the server re-priced from the new rate — surfaced on
 * `SettingsPage` so an admin sees the blast radius of the change they just made.
 */
export async function updateVatRate(
  ratePercent: number
): Promise<VatRateData & { restatedRows: number }> {
  const res = await put<{ success: boolean; data: VatRateData & { restatedRows: number } }>(
    '/settings/vat-rate',
    { ratePercent }
  );
  return res.data;
}

/** VAT-rate change log, newest first. */
export async function getVatRateHistory(): Promise<VatRateHistoryEntry[]> {
  const res = await get<{ success: boolean; data: VatRateHistoryEntry[] }>('/settings/vat-rate/history');
  return res.data || [];
}

/**
 * The competitor-markup warning threshold (D97): a quotation line whose
 * competitor price marks up our cost by LESS than this percent is flagged red.
 * The server serves 20 when it was never set.
 */
export async function getCompetitorMarkupWarn(): Promise<{ percent: number }> {
  const res = await get<{ success: boolean; data: { percent: number } }>('/settings/competitor-markup-warn');
  return res.data;
}

/** Set it. 400 `COMPETITOR_MARKUP_WARN_INVALID` when not a finite number 0-1000. */
export async function updateCompetitorMarkupWarn(percent: number): Promise<{ percent: number }> {
  const res = await put<{ success: boolean; data: { percent: number } }>(
    '/settings/competitor-markup-warn',
    { percent }
  );
  return res.data;
}

export async function getIfsRfqSettings(): Promise<IfsRfqSettings> {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/settings/ifs-rfq`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }

    const data: IfsRfqSettingsResponse = await response.json();

    if (!data.success) {
      throw new Error('Failed to fetch IFS RFQ settings');
    }

    return data.data;
  } catch (error: any) {
    console.error('Error fetching IFS RFQ settings:', error);
    throw new Error(error.message || 'Failed to fetch IFS RFQ settings');
  }
}

export async function updateIfsRfqSettings(
  settings: IfsRfqSettings
): Promise<IfsRfqSettings> {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/settings/ifs-rfq`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(settings),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }

    const data: IfsRfqSettingsResponse = await response.json();

    if (!data.success) {
      throw new Error('Failed to update IFS RFQ settings');
    }

    return data.data;
  } catch (error: any) {
    console.error('Error updating IFS RFQ settings:', error);
    throw new Error(error.message || 'Failed to update IFS RFQ settings');
  }
}

export async function getConfirmedOrderSettings(): Promise<ConfirmedOrderSettings> {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/settings/confirmed-orders`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }

    const data: ConfirmedOrderSettingsResponse = await response.json();

    if (!data.success) {
      throw new Error('Failed to fetch confirmed order settings');
    }

    return data.data;
  } catch (error: any) {
    console.error('Error fetching confirmed order settings:', error);
    throw new Error(error.message || 'Failed to fetch confirmed order settings');
  }
}

export async function updateConfirmedOrderSettings(
  settings: ConfirmedOrderSettings
): Promise<ConfirmedOrderSettings> {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/settings/confirmed-orders`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(settings),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }

    const data: ConfirmedOrderSettingsResponse = await response.json();

    if (!data.success) {
      throw new Error('Failed to update confirmed order settings');
    }

    return data.data;
  } catch (error: any) {
    console.error('Error updating confirmed order settings:', error);
    throw new Error(error.message || 'Failed to update confirmed order settings');
  }
}

export async function getEmailInboxes(): Promise<{
  inboxes: EmailInboxConfig[];
  subjectRules: EmailSubjectRule[];
}> {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/settings/email-inboxes`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }

    const data: EmailInboxSettingsResponse = await response.json();

    if (!data.success) {
      throw new Error('Failed to fetch email inbox settings');
    }

    return {
      inboxes: data.data.inboxes || [],
      subjectRules: Array.isArray(data.data.subjectRules) ? data.data.subjectRules : [],
    };
  } catch (error: any) {
    console.error('Error fetching email inbox settings:', error);
    throw new Error(error.message || 'Failed to fetch email inbox settings');
  }
}

export async function updateEmailInboxes(payload: {
  inboxes: Array<
    Omit<EmailInboxConfig, 'imap'> & {
      imap: Omit<EmailInboxConfig['imap'], 'hasPassword'> & { password?: string };
    }
  >;
  subjectRules?: EmailSubjectRule[];
}): Promise<{ inboxes: EmailInboxConfig[]; subjectRules: EmailSubjectRule[] }> {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${API_BASE_URL}/settings/email-inboxes`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Request failed with status ${response.status}`);
    }

    const data: EmailInboxSettingsResponse = await response.json();

    if (!data.success) {
      throw new Error('Failed to update email inbox settings');
    }

    return {
      inboxes: data.data.inboxes || [],
      subjectRules: Array.isArray(data.data.subjectRules) ? data.data.subjectRules : [],
    };
  } catch (error: any) {
    console.error('Error updating email inbox settings:', error);
    throw new Error(error.message || 'Failed to update email inbox settings');
  }
}

export async function getInboxClassifierSettings(): Promise<InboxClassifierSettings> {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  const response = await fetch(`${API_BASE_URL}/settings/inbox-classifier`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Request failed with status ${response.status}`);
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error('Failed to fetch inbox classifier settings');
  }
  return data.data as InboxClassifierSettings;
}

export async function updateInboxClassifierSettings(
  settings: InboxClassifierSettings
): Promise<InboxClassifierSettings> {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  const response = await fetch(`${API_BASE_URL}/settings/inbox-classifier`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(settings),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Request failed with status ${response.status}`);
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error('Failed to update inbox classifier settings');
  }
  return data.data as InboxClassifierSettings;
}

